import { useEffect, useState } from "react";
import { ACTIVE_FILE_PREVIEW_SIGNED_URL } from "../config/endpoint";

// Session-lived cache for private file URLs (the same generate_file?key=... shape
// useAuthenticatedFileUrl.js resolves), keyed by the key= query param -- so N callers
// asking for the same photo share one signed-URL round trip and one image fetch instead
// of N. Deliberately NOT shared with useAuthenticatedFileUrl.js (that hook's per-instance
// fetch-and-revoke-on-unmount behavior is correct for its own callers, e.g. lesson/PDF
// previews, and is left untouched) -- the key extractor below is a small, intentional
// duplicate of that hook's private helper.
const cache = new Map(); // key -> { blobUrl } | { promise }

const extractKeyFromGenerateFileUrl = (sourceUrl) => {
  try {
    return new URL(sourceUrl, window.location.origin).searchParams.get("key");
  } catch {
    return null;
  }
};

// Logout's one place to drop everything this cache is holding (blob: URLs don't
// survive a reload anyway, but this frees them promptly rather than leaving it to
// navigation teardown).
export function clearCachedFileUrls() {
  cache.forEach((entry) => {
    if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl);
  });
  cache.clear();
}

const resolveBlobUrl = (key) =>
  fetch(`${ACTIVE_FILE_PREVIEW_SIGNED_URL}?key=${encodeURIComponent(key)}`, {
    credentials: "include",
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to resolve file URL (${response.status})`);
      return response.json();
    })
    .then((data) => {
      const signedUrl = data?.message?.url;
      if (!signedUrl) throw new Error("No signed URL returned");
      // Deliberately no credentials here -- see useAuthenticatedFileUrl.js's own comment:
      // a presigned S3 URL needs none, and the bucket's wildcard-origin CORS policy
      // forbids combining one with a credentialed request.
      return fetch(signedUrl);
    })
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to load file (${response.status})`);
      return response.blob();
    })
    .then((blob) => URL.createObjectURL(blob));

// Starts (and caches) the fetch for a key with no cached blobUrl yet. A failure is never
// left poisoning the cache -- it's removed again so the next mount gets a fresh attempt --
// but every caller already awaiting this one in-flight promise still sees the rejection.
const fetchAndCache = (key) => {
  const entry = {};
  entry.promise = resolveBlobUrl(key)
    .then((blobUrl) => {
      cache.set(key, { blobUrl });
      return blobUrl;
    })
    .catch((error) => {
      if (cache.get(key) === entry) cache.delete(key);
      throw error;
    });
  cache.set(key, entry);
  return entry.promise;
};

export default function useCachedFileUrl(fileUrl) {
  const key = fileUrl ? extractKeyFromGenerateFileUrl(fileUrl) : null;
  const cached = key ? cache.get(key) : null;
  const [blobUrl, setBlobUrl] = useState(cached?.blobUrl || null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!key) {
      setBlobUrl(null);
      setError(null);
      return undefined;
    }

    const existing = cache.get(key);
    if (existing?.blobUrl) {
      setBlobUrl(existing.blobUrl);
      setError(null);
      return undefined;
    }

    setError(null);
    const promise = existing?.promise || fetchAndCache(key);
    promise
      .then((url) => {
        if (!cancelled) setBlobUrl(url);
      })
      .catch((fetchError) => {
        if (!cancelled) {
          setBlobUrl(null);
          setError(fetchError);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [key]);

  return { blobUrl, error };
}
